import type { ReactNode } from 'react';
import { Stack, Group, Text, Badge, Alert, Card, Table, Paper, Loader } from '@mantine/core';
import { IconAlertCircle, IconRocket } from '@tabler/icons-react';
import {
  FACTORY_BUILD_STATUS_LABELS,
  FACTORY_BUILD_STATUS_COLORS,
  FACTORY_SUBMISSION_STATE_LABELS,
  FACTORY_SUBMISSION_STATE_COLORS,
  type FactoryStoreKey,
} from '@/lib/constants/appFactory';
import { APP_STATE_TYPE_ID } from '@/lib/constants/app';
import { APP_PLATFORM_INFO, type AppPlatformKey } from '@/lib/constants/appPlatform';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useHelpTokens } from '@/features/help/useHelpTokens';
import type { AppBuildVersion, AppCfgsDto } from '@/lib/types/App';
import { AppFactoryUtils } from '../AppFactoryUtils';
import { AppPlatformUtils } from '../AppPlatformUtils';
import { StoreAccountUtils } from '../StoreAccountUtils';
import { StoreAppCheckUtils } from '../StoreAppCheckUtils';
import { useAppLegal } from '../legal/useAppLegal';
import { AppBuildRequestCard } from './AppBuildRequestCard';
import { AppSubmissionDetail } from './AppSubmissionDetail';
import { StoreReadinessCard } from './StoreReadinessCard';
import type { WizardStep } from './AppRokuSetupWizard';
import {
  useFactoryBuilds,
  useFactoryReleases,
  useFactorySubmissions,
  useFactoryTasks,
  useIsFactoryOperator,
  useStoreAccounts,
} from '../useAppFactory';
import { useStoreAppCheck } from '../useStoreAppCheck';
import { formatTimestamp } from '@/shared/utils/date';

interface AppPlatformStatusTabProps {
  appCfgId: number;
  app: AppCfgsDto;
  /** The factory platform whose builds this tab shows ('roku', 'firetv'). */
  platform: string;
  /** The store those builds are submitted to. */
  store: FactoryStoreKey;
  /** Stores on the readiness card (Roku's tab lists every store the app onboards). */
  readinessStores: FactoryStoreKey[];
  /** Builds from before App Center (Roku only; other platforms have none). */
  legacyBuildVersions?: AppBuildVersion[];
  /** Opens the setup wizard at a step (the Store readiness chips). */
  onOpenWizard: (step: WizardStep) => void;
  /** A platform's own card between the live version and the readiness card (Amazon's first-submission guide). */
  intro?: ReactNode;
  /** The parent app is Active (the Build card is off otherwise); default true. */
  isAppActive?: boolean;
}

/**
 * The customer-facing App Center status surface of ONE platform: the Build
 * card, current live version, action-needed tasks, submission status, build
 * history, and the release changelog feed. Since 3.34.0 the account holder
 * REQUESTS builds here (owner ruling 2026-09-23; the farm's claim is idempotent
 * and admission-gated); version UPGRADES of the fleet still ship through
 * operator-run campaigns — there is no customer upgrade button.
 */
export function AppPlatformStatusTab({
  appCfgId,
  app,
  platform,
  store,
  readinessStores,
  legacyBuildVersions = [],
  onOpenWizard,
  intro,
  isAppActive = true,
}: AppPlatformStatusTabProps) {
  const { selectedCId, selectedCustomer } = useAuth();
  const buildsQuery = useFactoryBuilds(appCfgId);
  const submissionsQuery = useFactorySubmissions(appCfgId);
  const releasesQuery = useFactoryReleases();
  const tasksQuery = useFactoryTasks();
  const storeAccountsQuery = useStoreAccounts();
  const legalQuery = useAppLegal(appCfgId);
  // The farm's verdict on the saved store app id (ADR-0029): asked only for a
  // platform that has a check and a row that carries an id — Roku never asks.
  const checkInfo = StoreAppCheckUtils.infoFor(platform);
  const checkPlatform = checkInfo ? APP_PLATFORM_INFO[checkInfo.platform] : null;
  const storeAppCheckQuery = useStoreAppCheck(
    appCfgId,
    platform,
    checkPlatform ? AppPlatformUtils.rowFor(app, checkPlatform.typeId)?.storeAppId : undefined,
  );
  const isOperator = useIsFactoryOperator();
  const { supportContact } = useHelpTokens();

  const isLoading =
    buildsQuery.isLoading ||
    submissionsQuery.isLoading ||
    releasesQuery.isLoading ||
    tasksQuery.isLoading ||
    // who submits decides what is a task for the customer: no flicker of a task that is not theirs
    storeAccountsQuery.isLoading;
  const unavailable = buildsQuery.isError || submissionsQuery.isError;

  if (isLoading) {
    return (
      <Group justify="center" p="xl">
        <Loader size="sm" />
        <Text c="dimmed" size="sm">
          Loading build status...
        </Text>
      </Group>
    );
  }

  if (unavailable) {
    return (
      <Stack gap="lg">
        <Alert icon={<IconAlertCircle size={16} />} color="gray">
          Build and submission status is currently unavailable. Your app settings are not
          affected.
        </Alert>
        <LegacyBuilds legacyBuildVersions={legacyBuildVersions} />
      </Stack>
    );
  }

  // The reads are per app; this tab shows its own platform's slice.
  const appBuilds = buildsQuery.data?.builds ?? [];
  const appSubmissions = submissionsQuery.data?.submissions ?? [];
  const builds = AppFactoryUtils.sortByCreatedDesc(
    AppFactoryUtils.buildsForPlatform(appBuilds, platform),
  );
  const submissions = AppFactoryUtils.sortByCreatedDesc(
    AppFactoryUtils.submissionsForStore(appSubmissions, store),
  );
  const releases = AppFactoryUtils.sortByCreatedDesc(
    AppFactoryUtils.releasesForPlatform(releasesQuery.data?.releases ?? [], platform),
  );
  const liveInfo = AppFactoryUtils.currentLiveInfo(submissions, builds, releases);
  const activeSubmissions = submissions.filter((s) => s.state !== 'superseded');
  const pastSubmissions = submissions.filter((s) => s.state === 'superseded');
  const releaseById = new Map(releases.map((r) => [r.shellReleaseId, r]));
  const buildById = new Map(builds.map((b) => [b.appBuild2Id, b]));
  const semverOfBuild = (appBuild2Id: string) =>
    releaseById.get(buildById.get(appBuild2Id)?.shellReleaseId ?? '')?.semver;
  // Farm liveness chip: absent unless the status payload carries the farm
  // field (daemons since the AppFactory split).
  const farm = AppFactoryUtils.farmLiveness(tasksQuery.data?.farm, Date.now());
  // The Build card's facts: the platform's label and whether its own row is on.
  // Roku keeps its own table; every other platform is an app_cfg_platform row.
  const platformInfo = platform === 'roku' ? null : APP_PLATFORM_INFO[platform as AppPlatformKey];
  const platformLabel = platform === 'roku' ? 'Roku' : (platformInfo?.label ?? platform);
  const isPlatformActive =
    platform === 'roku'
      ? app.roku?.core.appStateTypeId === APP_STATE_TYPE_ID.Active
      : platformInfo != null &&
        AppPlatformUtils.rowFor(app, platformInfo.typeId)?.appStateTypeId === APP_STATE_TYPE_ID.Active;
  // Store readiness (4.2): the account covering this app per store, own row first.
  const storeAccounts = storeAccountsQuery.data?.storeAccounts ?? [];
  const parentCIds = selectedCustomer?.cfg.parentCId ? [selectedCustomer.cfg.parentCId] : [];
  const accountFor = (s: string) =>
    StoreAccountUtils.resolve(storeAccounts, selectedCId ?? 0, parentCIds, s);
  // A submission the operator submits on its own is not a task for the customer.
  const submissionState = new Map(appSubmissions.map((s) => [s.appSubmissionId, s.state]));
  const appTasks = AppFactoryUtils.tasksForPlatform(
    AppFactoryUtils.tasksForApp(tasksQuery.data?.tasks ?? [], appCfgId),
    appBuilds,
    appSubmissions,
    platform,
    store,
  ).filter(
    (t) =>
      t.kind !== 'submission' ||
      !StoreAccountUtils.isOperatorHandled(
        accountFor(store),
        store,
        submissionState.get(t.id) ?? t.state,
      ),
  );
  const showBuildNumbers = builds.some((b) => b.versionCode != null || !!b.sourceRef);
  // Never part of isLoading / unavailable above: the line is simply absent until (and unless) it answers.
  const storeAppCheckLine =
    checkInfo && checkPlatform
      ? StoreAppCheckUtils.line(storeAppCheckQuery.data, storeAppCheckQuery.isError, {
          isOperator,
          farmStale: farm?.stale ?? false,
          copy: {
            idLabel: checkPlatform.storeAppIdLabel,
            storeName: checkInfo.storeName,
            consoleName: checkInfo.consoleName,
          },
          supportContact,
          accountLabel: accountFor(checkPlatform.store)?.accountLabel ?? '',
        })
      : null;

  return (
    <Stack gap="lg">
      {/* Current live version */}
      <Card withBorder radius="md" p="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Group gap="md">
            <IconRocket size={28} color="var(--mantine-color-green-6)" />
            {liveInfo ? (
            <div>
              <Group gap="xs">
                <Text fw={600}>Version {liveInfo.release?.semver ?? '—'}</Text>
                <Badge variant="light" color="green" size="sm">
                  Live
                </Badge>
                {liveInfo.release && (
                  <Badge variant="outline" color="gray" size="sm">
                    Lane {liveInfo.release.lane}
                  </Badge>
                )}
              </Group>
              <Text size="sm" c="dimmed">
                Live in the store since {AppFactoryUtils.formatWhen(liveInfo.submission.decidedAt)}
              </Text>
            </div>
          ) : (
            <div>
              <Text fw={600}>Not live yet</Text>
              <Text size="sm" c="dimmed">
                This app has not gone live through App Center. Status appears here as builds
                and submissions progress.
              </Text>
            </div>
            )}
          </Group>
          {farm && (
            <Badge variant="light" color={farm.stale ? 'orange' : 'green'} size="sm">
              {farm.label}
            </Badge>
          )}
        </Group>
      </Card>

      {/* Holder-requested build (3.34.0): the farm's idempotent, admission-gated claim */}
      <AppBuildRequestCard
        appCfgId={appCfgId}
        platform={platform}
        platformLabel={platformLabel}
        isAppActive={isAppActive}
        isPlatformActive={isPlatformActive}
        hasAddress={app.web != null}
        builds={appBuilds}
        releases={releasesQuery.data?.releases ?? []}
        submissions={appSubmissions}
        store={store}
        farmStale={farm?.stale ?? false}
      />

      {intro}

      {/* Store readiness: the developer account per store + the privacy policy (B1) */}
      <StoreReadinessCard
        app={app}
        stores={readinessStores}
        storeAccounts={storeAccounts}
        legal={legalQuery.data?.current ?? null}
        unavailable={storeAccountsQuery.isError}
        storeAppCheck={
          storeAppCheckLine && checkPlatform
            ? { store: checkPlatform.store, line: storeAppCheckLine }
            : null
        }
        onOpenWizard={onOpenWizard}
      />

      {/* Action needed */}
      {appTasks.length > 0 && (
        <Stack gap="xs">
          <Text fw={600} size="md">
            Action Needed
          </Text>
          {appTasks.map((t) => (
            <Alert
              key={`${t.kind}-${t.id}`}
              icon={<IconAlertCircle size={16} />}
              color="orange"
            >
              {t.description}
            </Alert>
          ))}
        </Stack>
      )}

      {/* Submissions: full self-serve detail for everything current; a
          compact history table for superseded rows (Tier 1, sprint 2.1) */}
      {activeSubmissions.length > 0 && (
        <Stack gap="xs">
          <Text fw={600} size="md">
            Store Submissions
          </Text>
          {activeSubmissions.map((s) => (
            <AppSubmissionDetail
              key={s.appSubmissionId}
              submission={s}
              appCfgId={appCfgId}
              app={app}
              build={buildById.get(s.appBuild2Id) ?? null}
              buildSemver={semverOfBuild(s.appBuild2Id)}
              storeAccount={accountFor(s.store)}
            />
          ))}
        </Stack>
      )}

      {pastSubmissions.length > 0 && (
        <Stack gap="xs">
          <Text fw={600} size="md">
            Past Submissions
          </Text>
          <Paper withBorder style={{ overflow: 'clip' }}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Store</Table.Th>
                  <Table.Th>Version</Table.Th>
                  <Table.Th>Submitted</Table.Th>
                  <Table.Th>Decided</Table.Th>
                  <Table.Th>Replaced by</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {pastSubmissions.map((s) => {
                  // B6: which newer submission displaced this one
                  const replacedBy = StoreAccountUtils.replacedBy(s, submissions);
                  const replacedSemver = replacedBy ? semverOfBuild(replacedBy.appBuild2Id) : undefined;
                  return (
                    <Table.Tr key={s.appSubmissionId}>
                      <Table.Td>
                        <Badge
                          variant="light"
                          color={FACTORY_SUBMISSION_STATE_COLORS[s.state] ?? 'gray'}
                          size="sm"
                        >
                          {FACTORY_SUBMISSION_STATE_LABELS[s.state] ?? s.state}
                        </Badge>
                      </Table.Td>
                      <Table.Td>{s.store}</Table.Td>
                      <Table.Td>{semverOfBuild(s.appBuild2Id) ?? '—'}</Table.Td>
                      <Table.Td>{AppFactoryUtils.formatWhen(s.submittedAt)}</Table.Td>
                      <Table.Td>{AppFactoryUtils.formatWhen(s.decidedAt)}</Table.Td>
                      <Table.Td>
                        {replacedBy
                          ? `${replacedSemver ? `v${replacedSemver}` : 'a newer build'} (build ${replacedBy.appBuild2Id})`
                          : '—'}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Paper>
        </Stack>
      )}

      {/* Build history */}
      {builds.length > 0 && (
        <Stack gap="xs">
          <Text fw={600} size="md">
            Build History
          </Text>
          <Paper withBorder style={{ overflow: 'clip' }}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Version</Table.Th>
                  <Table.Th>Platform</Table.Th>
                  {showBuildNumbers && <Table.Th>Build number</Table.Th>}
                  {showBuildNumbers && <Table.Th>Source</Table.Th>}
                  <Table.Th>Started</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {builds.map((b) => (
                  <Table.Tr key={b.appBuild2Id}>
                    <Table.Td>
                      <Badge
                        variant="light"
                        color={FACTORY_BUILD_STATUS_COLORS[b.status] ?? 'gray'}
                        size="sm"
                      >
                        {FACTORY_BUILD_STATUS_LABELS[b.status] ?? b.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td>{releaseById.get(b.shellReleaseId)?.semver ?? '—'}</Table.Td>
                    <Table.Td>{b.platform}</Table.Td>
                    {showBuildNumbers && <Table.Td>{b.versionCode ?? '—'}</Table.Td>}
                    {showBuildNumbers && (
                      <Table.Td>
                        <Text size="sm" ff="monospace">
                          {b.sourceRef ? b.sourceRef.slice(0, 7) : '—'}
                        </Text>
                      </Table.Td>
                    )}
                    <Table.Td>{AppFactoryUtils.formatWhen(b.created)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        </Stack>
      )}

      {/* Release notes feed — no upgrade button by design */}
      {releases.length > 0 && (
        <Stack gap="xs">
          <Text fw={600} size="md">
            Release Notes
          </Text>
          {releases.map((r) => (
            <Card key={r.shellReleaseId} withBorder radius="md" p="md">
              <Group gap="xs" mb={4}>
                <Text fw={600} size="sm">
                  {r.semver}
                </Text>
                <Badge variant="outline" color="gray" size="xs">
                  Lane {r.lane}
                </Badge>
                <Text size="xs" c="dimmed">
                  {AppFactoryUtils.formatWhen(r.created)}
                </Text>
              </Group>
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                {r.changelog}
              </Text>
            </Card>
          ))}
        </Stack>
      )}

      <LegacyBuilds legacyBuildVersions={legacyBuildVersions} />
    </Stack>
  );
}

// ── Internal: legacy (V1 pipeline) build history ──

function LegacyBuilds({ legacyBuildVersions }: { legacyBuildVersions: AppBuildVersion[] }) {
  if (legacyBuildVersions.length === 0) return null;
  const rows = [...legacyBuildVersions].sort((a, b) => b.build.created - a.build.created);
  return (
    <Stack gap="xs">
      <Text fw={600} size="md">
        Earlier Builds
      </Text>
      <Text size="xs" c="dimmed">
        Builds from before App Center.
      </Text>
      <Paper withBorder style={{ overflow: 'clip' }}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Version</Table.Th>
              <Table.Th>Created</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((bv) => (
              <Table.Tr key={bv.build.appBuildId}>
                <Table.Td>{bv.version.version}</Table.Td>
                <Table.Td>{formatTimestamp(bv.build.created)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>
    </Stack>
  );
}
